import { useState } from 'react';
import { Button, Card, Empty, Flex, Space, Tag, Typography } from 'antd';
import { EditOutlined, ExportOutlined, RedoOutlined, StopOutlined } from '@ant-design/icons';
import { Lead, LeadStatus } from './types';
import {
  canalLabel, COLUNAS_LEADS, situacaoProximoContato,
  STATUS_LEAD_CORES, STATUS_LEAD_LABELS,
} from './utils';

const { Text } = Typography;

export function KanbanLeads({
  leads,
  loading,
  mostrarDescartados,
  podeEditar,
  podeConverter,
  onEditar,
  onMudarStatus,
  onConverter,
}: {
  leads: Lead[];
  loading: boolean;
  mostrarDescartados: boolean;
  podeEditar: boolean;
  podeConverter: boolean;
  onEditar: (lead: Lead) => void;
  onMudarStatus: (lead: Lead, status: LeadStatus) => void;
  onConverter: (lead: Lead) => void;
}) {
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [destino, setDestino] = useState<LeadStatus | null>(null);
  const colunas: LeadStatus[] = mostrarDescartados
    ? [...COLUNAS_LEADS, 'descartado']
    : COLUNAS_LEADS;

  if (loading) return <Card loading style={{ minHeight: 300 }} />;

  return (
    <div className="leads-board-scroll">
      <div className="leads-board-grid" style={{
        gridTemplateColumns: `repeat(${colunas.length}, minmax(220px, 1fr))`,
        minWidth: colunas.length * 232,
      }}>
        {colunas.map(status => {
          const itens = leads.filter(lead => lead.status === status);
          const cores = STATUS_LEAD_CORES[status];
          const emDestino = destino === status;
          return (
            <Card
              key={status}
              size="small"
              className="leads-column"
              title={
                <Flex justify="space-between" align="center">
                  <Text strong style={{ color: cores.cor, fontSize: 11, textTransform: 'uppercase', letterSpacing: .6 }}>
                    {STATUS_LEAD_LABELS[status]}
                  </Text>
                  <Tag color={cores.cor} style={{ margin: 0 }}>{itens.length}</Tag>
                </Flex>
              }
              styles={{
                header: { borderBottom: `2px solid ${cores.cor}`, minHeight: 42 },
                body: { padding: 8 },
              }}
              style={{
                background: emDestino ? cores.fundo : '#FAFBFD',
                borderColor: emDestino ? cores.cor : undefined,
              }}
              onDragOver={e => { if (podeEditar) { e.preventDefault(); setDestino(status); } }}
              onDragLeave={() => setDestino(null)}
              onDrop={e => {
                e.preventDefault();
                const lead = leads.find(item => item.id === arrastando);
                if (lead && lead.status !== status) onMudarStatus(lead, status);
                setArrastando(null);
                setDestino(null);
              }}
            >
              <Space direction="vertical" size={8} style={{ width: '100%', minHeight: 235 }}>
                {itens.length === 0 && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Solte aqui" />
                )}
                {itens.map(lead => {
                  const agenda = situacaoProximoContato(lead.proximo_contato);
                  const nomeEmpresa = lead.cliente_fantasia || lead.cliente_nome;
                  const local = [lead.cliente_cidade, lead.cliente_uf].filter(Boolean).join('/');
                  return (
                    <Card
                      key={lead.id}
                      size="small"
                      hoverable={podeEditar}
                      draggable={podeEditar}
                      onDragStart={() => setArrastando(lead.id)}
                      onDragEnd={() => { setArrastando(null); setDestino(null); }}
                      onClick={() => podeEditar && onEditar(lead)}
                      style={{
                        borderLeft: `4px solid ${cores.cor}`,
                        opacity: arrastando === lead.id ? .55 : 1,
                        cursor: podeEditar ? 'pointer' : 'default',
                      }}
                      styles={{ body: { padding: 10 } }}
                    >
                      <Text strong style={{ display: 'block', color: '#020D7F', lineHeight: 1.25 }}>
                        {nomeEmpresa}
                      </Text>
                      <Text style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                        {lead.interesse}
                      </Text>
                      {(lead.contato_nome || local) && (
                        <Text type="secondary" style={{ display: 'block', fontSize: 10, marginTop: 5 }}>
                          {[lead.contato_nome, local].filter(Boolean).join(' · ')}
                        </Text>
                      )}
                      <Flex wrap="wrap" gap={4} style={{ marginTop: 7 }}>
                        {agenda && <Tag color={agenda.cor} style={{ margin: 0, fontSize: 10 }}>{agenda.label}</Tag>}
                        {lead.responsavel && <Tag style={{ margin: 0, fontSize: 10 }}>{lead.responsavel}</Tag>}
                        {lead.canal_origem && <Tag style={{ margin: 0, fontSize: 10 }}>{canalLabel(lead.canal_origem)}</Tag>}
                      </Flex>
                      <Flex justify="flex-end" gap={2} style={{ marginTop: 8 }}>
                        {status === 'qualificado' && (
                          <Button
                            size="small"
                            type="primary"
                            icon={<ExportOutlined />}
                            disabled={!podeConverter}
                            onClick={e => { e.stopPropagation(); onConverter(lead); }}
                          >
                            Pipeline
                          </Button>
                        )}
                        {podeEditar && status !== 'descartado' && (
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<StopOutlined />}
                            title="Descartar Lead"
                            onClick={e => { e.stopPropagation(); onMudarStatus(lead, 'descartado'); }}
                          />
                        )}
                        {podeEditar && status === 'descartado' && (
                          <Button
                            size="small"
                            type="text"
                            icon={<RedoOutlined />}
                            title="Reabrir como novo"
                            onClick={e => { e.stopPropagation(); onMudarStatus(lead, 'novo'); }}
                          />
                        )}
                        {podeEditar && (
                          <Button
                            size="small"
                            type="text"
                            icon={<EditOutlined />}
                            title="Editar Lead"
                            onClick={e => { e.stopPropagation(); onEditar(lead); }}
                          />
                        )}
                      </Flex>
                    </Card>
                  );
                })}
              </Space>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
